import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Users, Mail, Phone, Building2, Search, Tag, FileText, Clock,
  Plus, Trash2, Loader2, ChevronRight, ChevronLeft, X,
  DollarSign, Globe, Bot, UserPlus, Send, Eye, CalendarDays,
  StickyNote, TrendingUp, ArrowLeft, MessageSquare, Pencil, Save, UserCircle,
  MapPin, Briefcase, CheckSquare, Square, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  lead: { color: 'bg-blue-500', label: 'Lead', icon: <TrendingUp className="h-3.5 w-3.5" /> },
  client: { color: 'bg-emerald-500', label: 'Client', icon: <Users className="h-3.5 w-3.5" /> },
  vendor: { color: 'bg-amber-500', label: 'Vendor', icon: <Building2 className="h-3.5 w-3.5" /> },
  venue_owner: { color: 'bg-purple-500', label: 'Venue Owner', icon: <Building2 className="h-3.5 w-3.5" /> },
  agent: { color: 'bg-indigo-500', label: 'Agent', icon: <UserPlus className="h-3.5 w-3.5" /> },
  applicant: { color: 'bg-cyan-500', label: 'Applicant', icon: <UserPlus className="h-3.5 w-3.5" /> },
  staff: { color: 'bg-teal-500', label: 'Staff', icon: <Users className="h-3.5 w-3.5" /> },
  partner: { color: 'bg-fuchsia-500', label: 'Partner', icon: <Building2 className="h-3.5 w-3.5" /> },
  prospect: { color: 'bg-yellow-500', label: 'Prospect', icon: <TrendingUp className="h-3.5 w-3.5" /> },
};

// CRM tag categories — auto-generated when a ticket is bought + manually added
// by admins. The color hint helps admins eyeball a contact's profile at a
// glance: spend bucket (rose), music genres (fuchsia), event-specific (indigo).
const TAG_COLORS = {
  event: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  type: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  music: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30',
  food: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  city: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  spend: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  purchased: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  vip: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  press: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
};
const tagColor = (tag) => {
  const [cat] = (tag || '').split(':');
  return TAG_COLORS[cat] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
};

// Labels admins can pick from on a user-type contact. `lead` and `agent` are
// system-managed and intentionally omitted. `__role__` is a sentinel meaning
// "clear the override and use the underlying user role" — Radix Select can't
// hold a literal empty string as a value, so we translate it back to "" before
// hitting the API.
const LABEL_DEFAULT_SENTINEL = '__role__';
const LABEL_OPTIONS = [
  { value: LABEL_DEFAULT_SENTINEL, label: '— Use role —' },
  { value: 'client', label: 'Client' },
  { value: 'applicant', label: 'Applicant' },
  { value: 'staff', label: 'Staff' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'venue_owner', label: 'Venue Owner' },
  { value: 'partner', label: 'Partner' },
  { value: 'prospect', label: 'Prospect' },
];

const TIMELINE_ICONS = {
  outreach: <Send className="h-3.5 w-3.5" />,
  email_sent: <Mail className="h-3.5 w-3.5" />,
  sequence_created: <Bot className="h-3.5 w-3.5" />,
  proposal: <FileText className="h-3.5 w-3.5" />,
  booking: <CalendarDays className="h-3.5 w-3.5" />,
  note: <StickyNote className="h-3.5 w-3.5" />,
  staffing_application: <Briefcase className="h-3.5 w-3.5" />,
  application: <Briefcase className="h-3.5 w-3.5" />,
};

const TIMELINE_COLORS = {
  outreach: 'bg-blue-500',
  email_sent: 'bg-indigo-500',
  sequence_created: 'bg-violet-500',
  proposal: 'bg-amber-500',
  booking: 'bg-emerald-500',
  note: 'bg-slate-400',
  staffing_application: 'bg-cyan-500',
  application: 'bg-cyan-500',
};

export default function CRMDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const [allTags, setAllTags] = useState([]);

  // Contact detail
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('timeline');
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Create / Edit / Delete
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', company: '', event_type: '', budget: '', guest_count: '', notes: '', source: 'manual', status: 'new' });

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Businesses view
  const [view, setView] = useState('contacts'); // 'contacts' | 'businesses'
  const [businesses, setBusinesses] = useState([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null); // {company, employees}
  const [businessSearch, setBusinessSearch] = useState('');

  const LIMIT = 50;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch { /* ignore */ }
  }, [token]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/tags`, { headers: { Authorization: `Bearer ${token}` } });
      setAllTags(res.data);
    } catch { /* ignore */ }
  }, [token]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: LIMIT.toString() });
      if (typeFilter !== 'all') params.set('contact_type', typeFilter);
      if (search) params.set('search', search);
      if (tagFilter) params.set('tag', tagFilter);
      const res = await axios.get(`${API}/crm/contacts?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(res.data.contacts);
      setTotalContacts(res.data.total);
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [token, page, typeFilter, search, tagFilter]);

  useEffect(() => { fetchStats(); fetchTags(); }, [fetchStats, fetchTags]);
  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openContact = async (contact) => {
    setSelectedContact(contact);
    setDetailTab('timeline');
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/crm/contacts/${contact.contact_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContactDetail(res.data);
      // Sync selectedContact with the freshly fetched detail so the header
      // never shows stale list-row data (fixes the "details show blank until
      // I edit and save" bug — email/phone now appear immediately).
      const c = res.data?.contact;
      if (c) {
        setSelectedContact(prev => ({
          ...(prev || {}),
          name: c.name || prev?.name,
          email: c.email || prev?.email,
          phone: c.phone || prev?.phone,
          company: c.company || prev?.company,
          type: c.type || prev?.type,
          crm_label: c.crm_label ?? prev?.crm_label,
        }));
      }
    } catch {
      toast.error('Failed to load contact details');
      setContactDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedContact) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/crm/contacts/${selectedContact.contact_id}/notes`, { content: newNote }, { headers: { Authorization: `Bearer ${token}` } });
      setNewNote('');
      toast.success('Note added');
      // Refresh detail
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.contact_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContactDetail(res.data);
      fetchStats();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/crm/notes/${noteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Note deleted');
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.contact_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContactDetail(res.data);
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const addTag = async () => {
    if (!newTag.trim() || !contactDetail) return;
    const currentTags = contactDetail.tags || [];
    const cleanTag = newTag.trim().toLowerCase();
    if (currentTags.includes(cleanTag)) { setNewTag(''); return; }
    try {
      const res = await axios.post(`${API}/crm/contacts/${selectedContact.contact_id}/tags`, { tags: [...currentTags, cleanTag] }, { headers: { Authorization: `Bearer ${token}` } });
      setContactDetail(prev => ({ ...prev, tags: res.data.tags }));
      setNewTag('');
      fetchTags();
      toast.success('Tag added');
    } catch {
      toast.error('Failed to add tag');
    }
  };

  const removeTag = async (tag) => {
    try {
      const res = await axios.delete(`${API}/crm/contacts/${selectedContact.contact_id}/tags/${tag}`, { headers: { Authorization: `Bearer ${token}` } });
      setContactDetail(prev => ({ ...prev, tags: res.data.tags }));
      fetchTags();
    } catch {
      toast.error('Failed to remove tag');
    }
  };

  const createContact = async () => {
    if (!newContact.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/crm/contacts`, newContact, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact created');
      setShowCreateDialog(false);
      setNewContact({ name: '', email: '', phone: '', company: '', event_type: '', budget: '', guest_count: '', notes: '', source: 'manual', status: 'new' });
      fetchContacts();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create contact'); }
    finally { setSaving(false); }
  };

  const saveContactEdit = async () => {
    if (!selectedContact) return;
    setSaving(true);
    try {
      await axios.put(`${API}/crm/contacts/${selectedContact.contact_id}`, editFields, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact updated');
      setEditMode(false);
      setEditFields({});
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.contact_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContactDetail(res.data);
      // Keep `selectedContact` in sync — the header reads from it for instant
      // feedback so users see the new email/phone immediately, not after a
      // list refetch.
      const c = res.data?.contact;
      if (c) {
        setSelectedContact(prev => ({
          ...(prev || {}),
          name: c.name || prev?.name,
          email: c.email || prev?.email,
          phone: c.phone || prev?.phone,
          company: c.company || prev?.company,
          type: c.type || prev?.type,
          crm_label: c.crm_label ?? prev?.crm_label,
        }));
      }
      fetchContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  // Inline label update — admins can re-categorise a user-type contact
  // (e.g. promote an applicant to staff) without opening the edit form.
  const changeContactLabel = async (newLabel) => {
    if (!selectedContact) return;
    // Sentinel → empty string (clears override on the backend)
    const apiLabel = newLabel === LABEL_DEFAULT_SENTINEL ? '' : newLabel;
    try {
      const res = await axios.put(
        `${API}/crm/contacts/${selectedContact.contact_id}/label`,
        { label: apiLabel },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const effective = res.data.crm_label || res.data.role || 'client';
      setSelectedContact(prev => prev ? { ...prev, type: effective, crm_label: res.data.crm_label } : prev);
      setContactDetail(prev => prev?.contact
        ? { ...prev, contact: { ...prev.contact, type: effective, crm_label: res.data.crm_label } }
        : prev);
      toast.success(apiLabel ? `Label set to "${apiLabel}"` : 'Label cleared');
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update label');
    }
  };

  // Mutate a behavioural tag (add or remove). Tags are auto-applied on every
  // ticket purchase, but admins can hand-curate them too (vip, press, etc).
  const mutateContactTag = async ({ add = [], remove = [] }) => {
    if (!selectedContact) return;
    try {
      const res = await axios.put(
        `${API}/crm/contacts/${selectedContact.contact_id}/tags`,
        { add, remove },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const next = res.data.crm_tags || [];
      setSelectedContact(prev => prev ? { ...prev, crm_tags: next } : prev);
      setContactDetail(prev => prev?.contact ? { ...prev, contact: { ...prev.contact, crm_tags: next } } : prev);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tags');
    }
  };

  // Delete-contact dialog (None/'remove'/'account' for non-leads). For lead
  // contacts the delete is one-shot; for user contacts we ask the admin which
  // path to take.
  const [deleteDialog, setDeleteDialog] = useState(null); // null | { mode: 'lead'|'user', deleting?: 'remove'|'account' }

  const openDeleteContact = () => {
    if (!selectedContact) return;
    setDeleteDialog({ mode: selectedContact.type === 'lead' ? 'lead' : 'user' });
  };

  const runDeleteContact = async (mode) => {
    if (!selectedContact) return;
    setDeleteDialog((prev) => prev ? { ...prev, deleting: mode } : prev);
    try {
      // mode: 'lead' (one-shot for lead rows) | 'remove' | 'account'
      const apiMode = mode === 'remove' ? 'remove_from_crm' : mode === 'account' ? 'delete_account' : undefined;
      const url = `${API}/crm/contacts/${selectedContact.contact_id}` + (apiMode ? `?mode=${apiMode}` : '');
      await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(
        mode === 'account' ? 'Account deleted permanently'
        : mode === 'remove' ? 'Removed from CRM'
        : 'Contact deleted'
      );
      setDeleteDialog(null);
      setSelectedContact(null);
      setContactDetail(null);
      fetchContacts();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setDeleteDialog((prev) => prev ? { ...prev, deleting: undefined } : prev);
    }
  };

  const deleteContact = openDeleteContact;

  const startEdit = () => {
    if (!contactDetail?.contact) return;
    const c = contactDetail.contact;
    const emails = (c.emails && c.emails.length)
      ? c.emails.map(e => ({ value: e.value || '', label: e.label || 'other', is_primary: !!e.is_primary }))
      : (c.email ? [{ value: c.email, label: 'primary', is_primary: true }] : []);
    const phones = (c.phones && c.phones.length)
      ? c.phones.map(p => ({ value: p.value || '', label: p.label || 'other', is_primary: !!p.is_primary }))
      : (c.phone ? [{ value: c.phone, label: 'primary', is_primary: true }] : []);
    setEditFields({
      name: c.name || c.client_name || '',
      emails,
      phones,
      company: c.company || '',
      position: c.position || '',
      address: c.address || '',
      address_line2: c.address_line2 || '',
      city: c.city || '',
      state: c.state || '',
      zip: c.zip || '',
      event_type: c.event_type || '',
      budget: c.budget || '',
      guest_count: c.guest_count || '',
      notes: c.notes || '',
    });
    setDetailTab('info'); // auto-switch to Details tab so edit fields are visible
    setEditMode(true);
  };

  // Multi-email / multi-phone helpers used in the edit form.
  const updateEditList = (field, idx, patch) => {
    setEditFields(prev => {
      const list = [...(prev[field] || [])];
      list[idx] = { ...list[idx], ...patch };
      // Enforce exactly one primary.
      if (patch.is_primary === true) {
        list.forEach((it, i) => { it.is_primary = (i === idx); });
      }
      return { ...prev, [field]: list };
    });
  };
  const addEditListItem = (field) => {
    setEditFields(prev => {
      const list = [...(prev[field] || [])];
      const isFirst = list.length === 0;
      list.push({ value: '', label: field === 'emails' ? 'work' : 'mobile', is_primary: isFirst });
      return { ...prev, [field]: list };
    });
  };
  const removeEditListItem = (field, idx) => {
    setEditFields(prev => {
      const list = [...(prev[field] || [])];
      const wasPrimary = list[idx]?.is_primary;
      list.splice(idx, 1);
      if (wasPrimary && list.length > 0) list[0].is_primary = true;
      return { ...prev, [field]: list };
    });
  };

  // ---- Bulk selection ----
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const leadsOnPage = contacts.filter(c => c.type === 'lead').map(c => c.contact_id);
      const allSelected = leadsOnPage.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        leadsOnPage.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...leadsOnPage]);
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} contact${ids.length > 1 ? 's' : ''}? This can't be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/crm/contacts/bulk-delete`, { contact_ids: ids }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${res.data.deleted}${res.data.skipped ? `, skipped ${res.data.skipped} non-leads` : ''}`);
      clearSelection();
      fetchContacts();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ---- Businesses ----
  const fetchBusinesses = useCallback(async () => {
    setBusinessesLoading(true);
    try {
      const res = await axios.get(`${API}/crm/businesses`, { headers: { Authorization: `Bearer ${token}` } });
      setBusinesses(res.data.businesses || []);
    } catch {
      toast.error('Failed to load businesses');
    } finally { setBusinessesLoading(false); }
  }, [token]);
  useEffect(() => {
    if (view === 'businesses') fetchBusinesses();
  }, [view, fetchBusinesses]);
  const openBusiness = async (company) => {
    try {
      const res = await axios.get(`${API}/crm/businesses/employees?company=${encodeURIComponent(company)}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedBusiness(res.data);
    } catch {
      toast.error('Failed to load employees');
    }
  };
  const filteredBusinesses = businesses.filter(b =>
    !businessSearch || b.company.toLowerCase().includes(businessSearch.toLowerCase())
  );

  const totalPages = Math.ceil(totalContacts / LIMIT);

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(user?.role === 'sales_agent' ? '/sales' : '/admin')} data-testid="crm-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="crm-title">
                <MessageSquare className="h-8 w-8 text-indigo-500" />
                CRM
              </h1>
              <p className="text-muted-foreground mt-0.5">Manage all relationships across your platform</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="crm-new-contact-btn">
            <UserPlus className="h-4 w-4 mr-2" /> New Contact
          </Button>
        </div>

        {/* View toggle: Contacts | Businesses */}
        <div className="flex gap-1 mb-4 border border-border rounded-lg p-1 w-fit bg-muted/30" data-testid="crm-view-toggle">
          <button
            onClick={() => setView('contacts')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'contacts' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="crm-view-contacts"
          >
            <Users className="h-3.5 w-3.5 inline mr-1.5" /> Contacts
          </button>
          <button
            onClick={() => setView('businesses')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'businesses' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="crm-view-businesses"
          >
            <Building2 className="h-3.5 w-3.5 inline mr-1.5" /> Businesses
          </button>
        </div>

        {/* Bulk action toolbar */}
        {view === 'contacts' && selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-3 mb-3 p-3 rounded-lg border border-indigo-500/40 bg-indigo-500/5" data-testid="crm-bulk-toolbar">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-indigo-500 border-indigo-500/30">
                {selectedIds.size} selected
              </Badge>
              <button onClick={clearSelection} className="text-xs text-muted-foreground hover:text-foreground" data-testid="crm-bulk-clear">
                Clear selection
              </button>
            </div>
            <Button size="sm" variant="destructive" onClick={bulkDelete} disabled={bulkDeleting} data-testid="crm-bulk-delete-btn">
              {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
              Delete selected
            </Button>
          </div>
        )}

        {/* KPI Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="crm-stats">
            {[
              { label: 'Total Contacts', value: stats.total_contacts, color: 'text-foreground' },
              { label: 'Leads', value: stats.leads, color: 'text-blue-500' },
              { label: 'Clients', value: stats.clients, color: 'text-emerald-500' },
              { label: 'Vendors', value: stats.vendors, color: 'text-amber-500' },
              { label: 'Notes', value: stats.total_notes, color: 'text-slate-400' },
              { label: 'Tagged', value: stats.tagged_contacts, color: 'text-indigo-500' },
            ].map(kpi => (
              <Card key={kpi.label}>
                <CardContent className="pt-4 pb-3 px-4">
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search name, email, company..."
              className="pl-9"
              data-testid="crm-search"
            />
          </div>
          <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[150px]" data-testid="crm-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="vendor">Vendors</SelectItem>
              <SelectItem value="venue_owner">Venue Owners</SelectItem>
              <SelectItem value="agent">Agents</SelectItem>
            </SelectContent>
          </Select>
          {allTags.length > 0 && (
            <Select value={tagFilter} onValueChange={v => { setTagFilter(v === '_none' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="crm-tag-filter">
                <Tag className="h-3.5 w-3.5 mr-1.5" />
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">All Tags</SelectItem>
                {allTags.map(t => (
                  <SelectItem key={t.tag} value={t.tag}>{t.tag} ({t.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-3" data-testid="crm-count">
          {view === 'contacts' ? `${totalContacts} contact${totalContacts !== 1 ? 's' : ''} found` : `${filteredBusinesses.length} business${filteredBusinesses.length !== 1 ? 'es' : ''}`}
        </p>

        {/* Businesses view */}
        {view === 'businesses' && (
          <div className="space-y-4" data-testid="crm-businesses-view">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={businessSearch}
                onChange={e => setBusinessSearch(e.target.value)}
                placeholder="Search businesses..."
                className="pl-9"
                data-testid="crm-business-search"
              />
            </div>
            {businessesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : filteredBusinesses.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No businesses found</h3>
                  <p className="text-muted-foreground">Companies appear here after contacts set a Company field.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2" data-testid="crm-business-list">
                {filteredBusinesses.map(b => (
                  <div
                    key={b.company}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer transition-colors"
                    onClick={() => openBusiness(b.company)}
                    data-testid={`crm-business-${b.company}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{b.company}</p>
                      <p className="text-xs text-muted-foreground">
                        {b.employee_count} employee{b.employee_count !== 1 ? 's' : ''}
                        {b.lead_count > 0 && ` · ${b.lead_count} lead${b.lead_count !== 1 ? 's' : ''}`}
                        {b.user_count > 0 && ` · ${b.user_count} user${b.user_count !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contacts view */}
        {view === 'contacts' && (<>
        {/* Contact List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : contacts.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No contacts found</h3>
              <p className="text-muted-foreground">Adjust your filters or search criteria</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5" data-testid="crm-contact-list">
            {/* Select-all header for leads on this page */}
            {contacts.some(c => c.type === 'lead') && (
              <div className="flex items-center gap-2 px-3 pb-1 text-xs text-muted-foreground">
                <button
                  onClick={toggleSelectAll}
                  className="p-1 rounded hover:bg-muted"
                  data-testid="crm-select-all-btn"
                  title="Select all leads on this page"
                >
                  {contacts.filter(c => c.type === 'lead').every(c => selectedIds.has(c.contact_id)) && contacts.some(c => c.type === 'lead')
                    ? <CheckSquare className="h-4 w-4 text-indigo-500" />
                    : <Square className="h-4 w-4" />}
                </button>
                <span>Select all leads</span>
              </div>
            )}
            {contacts.map(contact => (
              <div
                key={contact.contact_id}
                className={`flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer transition-colors group ${selectedIds.has(contact.contact_id) ? 'bg-indigo-500/5 border-indigo-500/40' : ''}`}
                onClick={() => openContact(contact)}
                data-testid={`crm-contact-${contact.contact_id}`}
              >
                {/* Bulk-select checkbox (leads only) */}
                {contact.type === 'lead' ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(contact.contact_id); }}
                    className="p-1 rounded hover:bg-muted shrink-0"
                    data-testid={`crm-select-${contact.contact_id}`}
                    aria-label="Select contact for bulk actions"
                  >
                    {selectedIds.has(contact.contact_id)
                      ? <CheckSquare className="h-4 w-4 text-indigo-500" />
                      : <Square className="h-4 w-4 text-muted-foreground" />}
                  </button>
                ) : (
                  <span className="w-6 shrink-0" aria-hidden />
                )}

                {/* Avatar */}
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold ${TYPE_CONFIG[contact.type]?.color || 'bg-gray-400'}`}>
                  {(contact.name || '?')[0].toUpperCase()}
                </div>

                {/* Name & Type */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-semibold text-sm truncate">{contact.name}</p>
                    <Badge className={`${TYPE_CONFIG[contact.type]?.color || 'bg-gray-500'} text-white text-[10px] px-1.5 py-0`}>
                      {TYPE_CONFIG[contact.type]?.label || contact.type}
                    </Badge>
                    {contact.status && contact.type === 'lead' && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded capitalize">{contact.status}</span>
                    )}
                  </div>
                  {contact.company && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building2 className="h-3 w-3 shrink-0" /> {contact.company}
                    </p>
                  )}
                </div>

                {/* Email - prominent */}
                <div className="hidden md:flex items-center gap-1.5 text-sm min-w-0 max-w-[220px] shrink-0">
                  <Mail className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
                  <span className="truncate text-foreground">{contact.email || '—'}</span>
                </div>

                {/* Phone - prominent */}
                <div className="hidden lg:flex items-center gap-1.5 text-sm min-w-0 max-w-[160px] shrink-0">
                  <Phone className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  <span className="text-foreground">{contact.phone || '—'}</span>
                </div>

                {/* Budget */}
                <div className="text-right shrink-0 hidden sm:block w-20">
                  {contact.budget > 0 && (
                    <p className="text-sm font-bold text-emerald-500">${contact.budget.toLocaleString()}</p>
                  )}
                </div>

                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6" data-testid="crm-pagination">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
        </>)}

        {/* Contact Detail Modal */}
        <Dialog open={!!selectedContact} onOpenChange={(open) => { if (!open) { setSelectedContact(null); setContactDetail(null); setEditMode(false); setEditFields({}); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="crm-detail-modal">
            {(() => {
              // Always prefer the freshly fetched detail over the (possibly
              // stale) list-row data — fixes the "details blank until I edit"
              // bug.
              const c = contactDetail?.contact || selectedContact || {};
              const displayType = c.type || selectedContact?.type;
              const displayLabel = TYPE_CONFIG[displayType]?.label || (displayType ? displayType.replace(/_/g, ' ') : '');
              const displayColor = TYPE_CONFIG[displayType]?.color || 'bg-slate-500';
              const isLeadType = displayType === 'lead' || selectedContact?.type === 'lead';
              return (
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {TYPE_CONFIG[displayType]?.icon}
                {c.name || 'Contact Details'}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2">
                  {/* Prominent email & phone */}
                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-950/30 px-3 py-1.5 rounded-lg" data-testid="crm-detail-email">
                      <Mail className="h-4 w-4 text-indigo-500" />
                      <span className="text-sm font-medium text-foreground">{c.email || 'No email'}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-lg" data-testid="crm-detail-phone">
                      <Phone className="h-4 w-4 text-emerald-500" />
                      <span className="text-sm font-medium text-foreground">{c.phone || 'No phone'}</span>
                    </div>
                    {displayType && (
                      <Badge className={`${displayColor} text-white text-[10px]`} data-testid="crm-detail-type-badge">
                        {displayLabel}
                      </Badge>
                    )}
                    {/* Inline label override — only for user-type contacts */}
                    {!isLeadType && selectedContact?.contact_id && (
                      <div className="flex items-center gap-1.5" data-testid="crm-label-control">
                        <span className="text-[11px] text-muted-foreground">Label:</span>
                        <Select
                          value={c.crm_label ? c.crm_label : LABEL_DEFAULT_SENTINEL}
                          onValueChange={(v) => changeContactLabel(v)}
                        >
                          <SelectTrigger className="h-7 text-xs w-36" data-testid="crm-label-select">
                            <SelectValue placeholder="Use role" />
                          </SelectTrigger>
                          <SelectContent>
                            {LABEL_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value} data-testid={`crm-label-opt-${opt.value}`}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  {/* Edit / Delete buttons */}
                  <div className="flex items-center gap-2 pt-1">
                    {!editMode ? (
                      <Button size="sm" variant="outline" onClick={startEdit} data-testid="crm-edit-btn">
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" onClick={saveContactEdit} disabled={saving} data-testid="crm-save-btn">
                          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditMode(false); setEditFields({}); }}>Cancel</Button>
                      </>
                    )}
                    {displayType && (
                      <Button size="sm" variant="destructive" onClick={openDeleteContact} data-testid="crm-delete-btn">
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                      </Button>
                    )}
                  </div>
                  {/* Behavioural tags — auto-generated on every ticket purchase
                      + admin-curated. Click an "x" to remove; type+Enter to add. */}
                  {!isLeadType && selectedContact?.contact_id && (
                    <div className="mt-2 pt-2 border-t border-border/60" data-testid="crm-tags-row">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Profile Tags</p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(c.crm_tags || []).length === 0 && (
                          <span className="text-[11px] text-muted-foreground italic">No tags yet — they'll auto-populate on the next ticket purchase.</span>
                        )}
                        {(c.crm_tags || []).map(tag => (
                          <span key={tag} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${tagColor(tag)}`} data-testid={`crm-tag-${tag}`}>
                            {tag}
                            <button
                              type="button"
                              onClick={() => mutateContactTag({ remove: [tag] })}
                              className="opacity-60 hover:opacity-100 -mr-0.5"
                              data-testid={`crm-tag-remove-${tag}`}
                              aria-label={`remove tag ${tag}`}
                            >×</button>
                          </span>
                        ))}
                        <input
                          type="text"
                          placeholder="+ Add tag (e.g. vip, press, regular)"
                          className="text-[11px] px-2 py-0.5 rounded-full border border-border bg-background w-44 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          data-testid="crm-tag-add-input"
                          onKeyDown={async (e) => {
                            if (e.key !== 'Enter') return;
                            const v = e.currentTarget.value.trim();
                            if (!v) return;
                            await mutateContactTag({ add: [v] });
                            e.currentTarget.value = '';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
              );
            })()}

            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
              </div>
            ) : contactDetail ? (
              <div className="space-y-4">
                {/* Tags */}
                <div className="flex flex-wrap items-center gap-1.5" data-testid="crm-tags-section">
                  <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                  {contactDetail.tags?.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs gap-1 pr-1">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="ml-0.5 hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <div className="flex items-center gap-1">
                    <Input
                      value={newTag}
                      onChange={e => setNewTag(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addTag()}
                      placeholder="Add tag..."
                      className="h-6 w-24 text-xs px-2"
                      data-testid="crm-add-tag-input"
                    />
                    <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={addTag}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Detail Tabs */}
                <div className="flex gap-1 border-b">
                  {[
                    { id: 'timeline', label: 'Timeline', icon: <Clock className="h-3.5 w-3.5" /> },
                    { id: 'info', label: 'Details', icon: <Users className="h-3.5 w-3.5" /> },
                    { id: 'notes', label: `Notes (${contactDetail.notes?.length || 0})`, icon: <StickyNote className="h-3.5 w-3.5" /> },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setDetailTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        detailTab === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`crm-tab-${tab.id}`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                {/* Timeline Tab */}
                {detailTab === 'timeline' && (
                  <div className="space-y-1" data-testid="crm-timeline">
                    {contactDetail.timeline?.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <Clock className="h-10 w-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No activity recorded yet</p>
                      </div>
                    ) : (
                      <div className="relative pl-6">
                        <div className="absolute left-2.5 top-0 bottom-0 w-px bg-border" />
                        {contactDetail.timeline?.map((item, idx) => (
                          <div key={idx} className="relative flex gap-3 pb-4" data-testid={`timeline-item-${idx}`}>
                            <div className={`absolute -left-3.5 w-5 h-5 rounded-full flex items-center justify-center text-white ${TIMELINE_COLORS[item.event] || 'bg-gray-400'}`}>
                              {TIMELINE_ICONS[item.event] || <Clock className="h-3 w-3" />}
                            </div>
                            <div className="flex-1 min-w-0 ml-4">
                              <div className="flex items-center gap-2 mb-0.5">
                                <Badge variant="outline" className="text-[10px] capitalize">{item.event?.replace(/_/g, ' ')}</Badge>
                                {item.outcome && (
                                  <Badge variant="outline" className={`text-[10px] ${
                                    item.outcome === 'positive' || item.outcome === 'sent' ? 'text-emerald-600 border-emerald-200' :
                                    item.outcome === 'negative' ? 'text-red-600 border-red-200' : ''
                                  }`}>{item.outcome}</Badge>
                                )}
                                <span className="text-[10px] text-muted-foreground ml-auto">
                                  {item.date ? new Date(item.date).toLocaleDateString() : ''}
                                </span>
                              </div>
                              {item.subject && <p className="text-sm font-medium truncate">{item.subject}</p>}
                              {item.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>}
                              {item.author && <p className="text-[10px] text-muted-foreground mt-0.5">by {item.author}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Info Tab */}
                {detailTab === 'info' && (
                  <div className="space-y-3" data-testid="crm-info">
                    {editMode ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Name</label>
                          <Input value={editFields.name || ''} onChange={e => setEditFields(prev => ({ ...prev, name: e.target.value }))} data-testid="crm-edit-name" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Position / Title</label>
                          <Input value={editFields.position || ''} onChange={e => setEditFields(prev => ({ ...prev, position: e.target.value }))} placeholder="e.g. Director of Events" data-testid="crm-edit-position" />
                        </div>

                        {/* Multiple emails */}
                        <div className="col-span-2" data-testid="crm-edit-emails-section">
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-muted-foreground">Emails</label>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => addEditListItem('emails')} data-testid="crm-add-email-btn">
                              <Plus className="h-3 w-3 mr-1" /> Add email
                            </Button>
                          </div>
                          <div className="space-y-1.5">
                            {(editFields.emails || []).map((em, idx) => (
                              <div key={idx} className="flex gap-1.5 items-center" data-testid={`crm-email-row-${idx}`}>
                                <button
                                  type="button"
                                  onClick={() => updateEditList('emails', idx, { is_primary: true })}
                                  className={`p-1 rounded ${em.is_primary ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500'}`}
                                  title={em.is_primary ? 'Primary' : 'Set as primary'}
                                  data-testid={`crm-email-primary-${idx}`}
                                >
                                  <Star className={`h-3.5 w-3.5 ${em.is_primary ? 'fill-amber-500' : ''}`} />
                                </button>
                                <Input value={em.value} onChange={e => updateEditList('emails', idx, { value: e.target.value })} placeholder="email@example.com" className="flex-1" data-testid={`crm-edit-email-${idx}`} />
                                <Input value={em.label} onChange={e => updateEditList('emails', idx, { label: e.target.value })} placeholder="work" className="w-24" data-testid={`crm-edit-email-label-${idx}`} />
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => removeEditListItem('emails', idx)} data-testid={`crm-remove-email-${idx}`}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ))}
                            {!(editFields.emails || []).length && (
                              <p className="text-xs text-muted-foreground">No emails — click Add email.</p>
                            )}
                          </div>
                        </div>

                        {/* Multiple phones */}
                        <div className="col-span-2" data-testid="crm-edit-phones-section">
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-muted-foreground">Phones</label>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => addEditListItem('phones')} data-testid="crm-add-phone-btn">
                              <Plus className="h-3 w-3 mr-1" /> Add phone
                            </Button>
                          </div>
                          <div className="space-y-1.5">
                            {(editFields.phones || []).map((ph, idx) => (
                              <div key={idx} className="flex gap-1.5 items-center" data-testid={`crm-phone-row-${idx}`}>
                                <button
                                  type="button"
                                  onClick={() => updateEditList('phones', idx, { is_primary: true })}
                                  className={`p-1 rounded ${ph.is_primary ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500'}`}
                                  title={ph.is_primary ? 'Primary' : 'Set as primary'}
                                  data-testid={`crm-phone-primary-${idx}`}
                                >
                                  <Star className={`h-3.5 w-3.5 ${ph.is_primary ? 'fill-amber-500' : ''}`} />
                                </button>
                                <Input value={ph.value} onChange={e => updateEditList('phones', idx, { value: e.target.value })} placeholder="+1 303-555-0100" className="flex-1" data-testid={`crm-edit-phone-${idx}`} />
                                <Input value={ph.label} onChange={e => updateEditList('phones', idx, { label: e.target.value })} placeholder="mobile" className="w-24" data-testid={`crm-edit-phone-label-${idx}`} />
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => removeEditListItem('phones', idx)} data-testid={`crm-remove-phone-${idx}`}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ))}
                            {!(editFields.phones || []).length && (
                              <p className="text-xs text-muted-foreground">No phones — click Add phone.</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Company</label>
                          <Input value={editFields.company || ''} onChange={e => setEditFields(prev => ({ ...prev, company: e.target.value }))} data-testid="crm-edit-company" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Event Type</label>
                          <Input value={editFields.event_type || ''} onChange={e => setEditFields(prev => ({ ...prev, event_type: e.target.value }))} data-testid="crm-edit-event-type" />
                        </div>

                        {/* Address */}
                        <div className="col-span-2">
                          <label className="text-xs text-muted-foreground mb-1 block">Address</label>
                          <Input value={editFields.address || ''} onChange={e => setEditFields(prev => ({ ...prev, address: e.target.value }))} placeholder="Street address" data-testid="crm-edit-address" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-muted-foreground mb-1 block">Address line 2 (optional)</label>
                          <Input value={editFields.address_line2 || ''} onChange={e => setEditFields(prev => ({ ...prev, address_line2: e.target.value }))} placeholder="Apt / Suite" data-testid="crm-edit-address2" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">City</label>
                          <Input value={editFields.city || ''} onChange={e => setEditFields(prev => ({ ...prev, city: e.target.value }))} data-testid="crm-edit-city" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">State</label>
                            <Input value={editFields.state || ''} onChange={e => setEditFields(prev => ({ ...prev, state: e.target.value }))} data-testid="crm-edit-state" />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Zip</label>
                            <Input value={editFields.zip || ''} onChange={e => setEditFields(prev => ({ ...prev, zip: e.target.value }))} data-testid="crm-edit-zip" />
                          </div>
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Budget</label>
                          <Input type="number" value={editFields.budget || ''} onChange={e => setEditFields(prev => ({ ...prev, budget: e.target.value }))} data-testid="crm-edit-budget" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Guest Count</label>
                          <Input type="number" value={editFields.guest_count || ''} onChange={e => setEditFields(prev => ({ ...prev, guest_count: e.target.value }))} data-testid="crm-edit-guest-count" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                          <Textarea value={editFields.notes || ''} onChange={e => setEditFields(prev => ({ ...prev, notes: e.target.value }))} rows={3} data-testid="crm-edit-notes" />
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Multi-emails section */}
                        {contactDetail.contact?.emails?.length > 0 && (
                          <div className="space-y-1.5" data-testid="crm-detail-emails">
                            <p className="text-xs text-muted-foreground">Emails</p>
                            {contactDetail.contact.emails.map((em, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-2.5 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-100 dark:border-indigo-900/30">
                                <Mail className="h-4 w-4 text-indigo-500 shrink-0" />
                                <p className="text-sm font-medium truncate flex-1">{em.value}</p>
                                <Badge variant="outline" className="text-[10px] capitalize">{em.label}</Badge>
                                {em.is_primary && <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Multi-phones section */}
                        {contactDetail.contact?.phones?.length > 0 && (
                          <div className="space-y-1.5" data-testid="crm-detail-phones">
                            <p className="text-xs text-muted-foreground">Phones</p>
                            {contactDetail.contact.phones.map((ph, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-2.5 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg border border-emerald-100 dark:border-emerald-900/30">
                                <Phone className="h-4 w-4 text-emerald-500 shrink-0" />
                                <p className="text-sm font-medium flex-1">{ph.value}</p>
                                <Badge variant="outline" className="text-[10px] capitalize">{ph.label}</Badge>
                                {ph.is_primary && <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          {contactDetail.contact?.position && (
                            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg" data-testid="crm-detail-position">
                              <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Position</p>
                                <p className="text-sm font-medium">{contactDetail.contact.position}</p>
                              </div>
                            </div>
                          )}
                          {contactDetail.contact?.company && (
                            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Company</p>
                                <p className="text-sm font-medium">{contactDetail.contact.company}</p>
                              </div>
                            </div>
                          )}
                          {(contactDetail.contact?.address || contactDetail.contact?.city) && (
                            <div className="col-span-2 flex items-start gap-2 p-3 bg-muted/50 rounded-lg" data-testid="crm-detail-address">
                              <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Address</p>
                                <p className="text-sm font-medium">
                                  {[contactDetail.contact.address, contactDetail.contact.address_line2].filter(Boolean).join(', ')}
                                  {(contactDetail.contact.address || contactDetail.contact.address_line2) && (contactDetail.contact.city || contactDetail.contact.state || contactDetail.contact.zip) && <br />}
                                  {[contactDetail.contact.city, contactDetail.contact.state, contactDetail.contact.zip].filter(Boolean).join(', ')}
                                </p>
                              </div>
                            </div>
                          )}
                          {contactDetail.contact?.event_type && (
                            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                              <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Event Type</p>
                                <p className="text-sm font-medium capitalize">{contactDetail.contact.event_type}</p>
                              </div>
                            </div>
                          )}
                          {contactDetail.contact?.budget > 0 && (
                            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                              <DollarSign className="h-4 w-4 text-emerald-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Budget</p>
                                <p className="text-sm font-bold text-emerald-500">${contactDetail.contact.budget.toLocaleString()}</p>
                              </div>
                            </div>
                          )}
                          {contactDetail.contact?.guest_count > 0 && (
                            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Guests</p>
                                <p className="text-sm font-medium">{contactDetail.contact.guest_count}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {contactDetail.contact?.notes && (
                          <div className="p-3 bg-muted/30 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-1 font-medium">Lead Notes</p>
                            <p className="text-sm whitespace-pre-wrap">{contactDetail.contact.notes}</p>
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                          <span>Source: {contactDetail.contact?.source || 'N/A'}</span>
                          <span>Created: {contactDetail.contact?.created_at ? new Date(contactDetail.contact.created_at).toLocaleDateString() : 'N/A'}</span>
                          {contactDetail.contact?.assigned_to && <span>Assigned: {contactDetail.contact.assigned_to}</span>}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Notes Tab */}
                {detailTab === 'notes' && (
                  <div className="space-y-3" data-testid="crm-notes">
                    {/* Add note */}
                    <div className="flex gap-2">
                      <Textarea
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        placeholder="Add a note about this contact..."
                        rows={2}
                        className="flex-1"
                        data-testid="crm-note-input"
                      />
                      <Button onClick={addNote} disabled={savingNote || !newNote.trim()} className="self-end" data-testid="crm-add-note-btn">
                        {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                        Add
                      </Button>
                    </div>

                    {/* Notes list */}
                    {contactDetail.notes?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <StickyNote className="h-10 w-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No notes yet. Add one above.</p>
                      </div>
                    ) : (
                      contactDetail.notes?.map(note => (
                        <div key={note.note_id} className="p-3 bg-muted/30 rounded-lg border group" data-testid={`crm-note-${note.note_id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm whitespace-pre-wrap flex-1">{note.content}</p>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0 text-red-500 hover:text-red-600"
                              onClick={() => deleteNote(note.note_id)}
                              data-testid={`crm-delete-note-${note.note_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                            <span>{note.author_name}</span>
                            <span>{new Date(note.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p>Failed to load contact details</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Contact Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg" data-testid="crm-create-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-indigo-500" />
                New Contact
              </DialogTitle>
              <DialogDescription>Add a new lead to your CRM.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
                <Input value={newContact.name} onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))} placeholder="Full name" data-testid="crm-create-name" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Email</label>
                <Input value={newContact.email} onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" data-testid="crm-create-email" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Phone</label>
                <Input value={newContact.phone} onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="crm-create-phone" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Company</label>
                <Input value={newContact.company} onChange={e => setNewContact(p => ({ ...p, company: e.target.value }))} placeholder="Company name" data-testid="crm-create-company" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Event Type</label>
                <Input value={newContact.event_type} onChange={e => setNewContact(p => ({ ...p, event_type: e.target.value }))} placeholder="Wedding, Corporate..." data-testid="crm-create-event-type" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Budget</label>
                <Input type="number" value={newContact.budget} onChange={e => setNewContact(p => ({ ...p, budget: e.target.value }))} placeholder="5000" data-testid="crm-create-budget" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Guest Count</label>
                <Input type="number" value={newContact.guest_count} onChange={e => setNewContact(p => ({ ...p, guest_count: e.target.value }))} placeholder="100" data-testid="crm-create-guest-count" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Source</label>
                <Select value={newContact.source} onValueChange={v => setNewContact(p => ({ ...p, source: v }))}>
                  <SelectTrigger data-testid="crm-create-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="cold_outreach">Cold Outreach</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                <Textarea value={newContact.notes} onChange={e => setNewContact(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Any notes about this contact..." data-testid="crm-create-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={createContact} disabled={saving || !newContact.name.trim()} data-testid="crm-create-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Create Contact
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete-contact dialog (lead one-shot OR user remove/account) */}
        <Dialog open={!!deleteDialog} onOpenChange={(open) => { if (!open) setDeleteDialog(null); }}>
          <DialogContent className="max-w-md" data-testid="crm-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-rose-500" />
                Delete {selectedContact?.name || 'contact'}?
              </DialogTitle>
              <DialogDescription>
                {deleteDialog?.mode === 'lead'
                  ? 'This will permanently remove the lead, along with their tags and notes. This cannot be undone.'
                  : `This contact is a registered ${TYPE_CONFIG[selectedContact?.type]?.label || 'user'} account. Choose how you want to handle them.`}
              </DialogDescription>
            </DialogHeader>
            {deleteDialog?.mode === 'user' && (
              <div className="space-y-3 mt-1">
                <div className="rounded-lg border border-border p-3 text-xs space-y-1">
                  <div className="font-semibold text-foreground">Remove from CRM</div>
                  <div className="text-muted-foreground">
                    Clears their tags &amp; notes from CRM. The user account stays — they can still sign in and use the platform.
                  </div>
                </div>
                <div className="rounded-lg border border-rose-500/40 p-3 text-xs space-y-1 bg-rose-500/5">
                  <div className="font-semibold text-rose-300">Delete account permanently</div>
                  <div className="text-muted-foreground">
                    Hard-deletes the user record + their vendor/provider profile + CRM data. Their historical events, bookings and products are preserved for audit. Cannot be undone.
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="ghost" onClick={() => setDeleteDialog(null)} disabled={!!deleteDialog?.deleting} data-testid="crm-delete-cancel">
                Cancel
              </Button>
              {deleteDialog?.mode === 'lead' ? (
                <Button
                  variant="destructive"
                  onClick={() => runDeleteContact('lead')}
                  disabled={!!deleteDialog?.deleting}
                  data-testid="crm-delete-confirm-lead"
                >
                  {deleteDialog?.deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete lead
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => runDeleteContact('remove')}
                    disabled={!!deleteDialog?.deleting}
                    data-testid="crm-delete-confirm-remove"
                  >
                    {deleteDialog?.deleting === 'remove' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Remove from CRM
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => runDeleteContact('account')}
                    disabled={!!deleteDialog?.deleting}
                    data-testid="crm-delete-confirm-account"
                  >
                    {deleteDialog?.deleting === 'account' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Delete account permanently
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Business Employees Drill-down Modal */}
        <Dialog open={!!selectedBusiness} onOpenChange={(open) => { if (!open) setSelectedBusiness(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="crm-business-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-amber-500" />
                {selectedBusiness?.company}
              </DialogTitle>
              <DialogDescription>
                {selectedBusiness?.total} employee{selectedBusiness?.total !== 1 ? 's' : ''} at this business
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-2">
              {selectedBusiness?.employees?.length ? selectedBusiness.employees.map(emp => (
                <div
                  key={emp.contact_id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => { setSelectedBusiness(null); openContact({ ...emp }); }}
                  data-testid={`crm-business-employee-${emp.contact_id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold ${TYPE_CONFIG[emp.type]?.color || 'bg-gray-400'}`}>
                    {(emp.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{emp.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      {emp.position && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{emp.position}</span>}
                      {emp.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{emp.email}</span>}
                      {emp.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{emp.phone}</span>}
                    </p>
                  </div>
                  <Badge className={`${TYPE_CONFIG[emp.type]?.color || 'bg-gray-500'} text-white text-[10px]`}>
                    {TYPE_CONFIG[emp.type]?.label || emp.type}
                  </Badge>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-6">No employees found.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
